// strokeLogTailRequest — the WS round-trip that fetches the un-flushed live
// tail of the room's session buffer for the timelapse feature (絵ログ live
// tail — growth-timelapse-share-2026-07-02.md §4-§5,
// feature/stroke-log-live-tail).
//
// The client sends `whiteboard.stroke-log-tail-request { sinceSeq }` and the
// server replies (unicast) with `whiteboard.stroke-log-tail { sinceSeq,
// toSeq, flushedAtMs, events }`. This helper wraps that single request /
// response as a `Promise`, correlating the reply by its echoed `sinceSeq`
// (so a stray/duplicate tail from another feature can't resolve the wrong
// request) and giving up after a timeout so the timelapse degrades to
// "R2 only" instead of hanging (§ backend hand-off: 失敗時は tail 無しで続行).
//
// It touches only the `Transport` port (never `WSClient` directly) so it is
// unit-testable with an in-memory fake, mirroring `useCases.ts`.

import type { WhiteboardStrokeLogTail } from '@oekaki/protocol';
import type { Transport } from './ports/Transport.js';

/** Default: give up after 5 s and fall back to the R2-only timeline. */
export const STROKE_LOG_TAIL_TIMEOUT_MS = 5_000;

export interface RequestStrokeLogTailOptions {
  /** Override the giving-up timeout (tests use a small value). */
  readonly timeoutMs?: number;
  /** Abort the wait (e.g. the modal unmounted). Resolves `null`. */
  readonly signal?: AbortSignal;
}

/** Why a tail round-trip did not produce a usable response. */
export type StrokeLogTailFailure = 'not-open' | 'aborted' | 'timeout' | 'send-failed' | 'invalid';

export type StrokeLogTailResult =
  | { readonly ok: true; readonly tail: WhiteboardStrokeLogTail }
  | { readonly ok: false; readonly reason: StrokeLogTailFailure };

const isUsableTail = (tail: WhiteboardStrokeLogTail): boolean =>
  Number.isFinite(tail.toSeq) && Array.isArray(tail.events);

/**
 * Same round-trip as `requestStrokeLogTail`, but tells *why* it failed so a
 * caller that must not silently miss the newest drawing (人を外して戻す) can
 * warn. Never rejects.
 */
export const requestStrokeLogTailResult = (
  transport: Transport,
  sinceSeq: number,
  options: RequestStrokeLogTailOptions = {},
): Promise<StrokeLogTailResult> => {
  // The tail is served over the client's own room socket; if it is not open
  // we are not in the room and the server would have nothing to answer with.
  if (transport.status.t !== 'open') return Promise.resolve({ ok: false, reason: 'not-open' });
  if (options.signal?.aborted === true) return Promise.resolve({ ok: false, reason: 'aborted' });

  const timeoutMs = options.timeoutMs ?? STROKE_LOG_TAIL_TIMEOUT_MS;

  return new Promise<StrokeLogTailResult>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let off: (() => void) | null = null;

    const settle = (value: StrokeLogTailResult): void => {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      off?.();
      options.signal?.removeEventListener('abort', onAbort);
      resolve(value);
    };

    const onAbort = (): void => settle({ ok: false, reason: 'aborted' });
    options.signal?.addEventListener('abort', onAbort, { once: true });

    off = transport.onEvent((event) => {
      // Correlate by the echoed `sinceSeq` — the response is unicast to us
      // but this de-risks a future concurrent tail request for a different
      // `sinceSeq`.
      if (event.t === 'whiteboard.stroke-log-tail' && event.sinceSeq === sinceSeq) {
        settle(isUsableTail(event) ? { ok: true, tail: event } : { ok: false, reason: 'invalid' });
      }
    });

    timer = setTimeout(() => settle({ ok: false, reason: 'timeout' }), timeoutMs);

    try {
      transport.send({ t: 'whiteboard.stroke-log-tail-request', sinceSeq });
    } catch {
      // send-before-open / serialisation quirk.
      settle({ ok: false, reason: 'send-failed' });
    }
  });
};

/**
 * Ask the WS server for the drawing events the client does not yet have
 * from R2 (= everything with `seq > sinceSeq`). Resolves with the tail
 * response, or `null` when the round-trip cannot complete — the socket is
 * not open (not currently in the room), the request throws, the wait is
 * aborted, or nothing answers within the timeout. Never rejects: a `null`
 * simply means "no live tail, use the R2 segments as-is". Callers that need
 * the failure reason use `requestStrokeLogTailResult`.
 */
export const requestStrokeLogTail = async (
  transport: Transport,
  sinceSeq: number,
  options: RequestStrokeLogTailOptions = {},
): Promise<WhiteboardStrokeLogTail | null> => {
  const r = await requestStrokeLogTailResult(transport, sinceSeq, options);
  return r.ok ? r.tail : null;
};
